import type { AlertRecord } from '@adiox/api';

/** A price alert: tell the user once when the bid or ask of a symbol reaches a price from below or above. */
export interface PriceAlert {
  id: string;
  symbol: string;
  field: 'bid' | 'ask';
  /** 'above': goes off when the price rises to the level or beyond; 'below': when it falls to it or beyond. */
  direction: 'above' | 'below';
  price: number;
  active: boolean;
  createdAt: string;
  /** When it went off (UTC ISO); an alert goes off once and then waits to be turned on again. */
  triggeredAt?: string;
  /** The price that set it off. */
  triggeredValue?: number;
}

/** The server keeps up to this many alerts per account. */
export const MAX_ALERTS = 50;

/** A server alert in the terminal's shape. */
export const fromServer = (r: AlertRecord): PriceAlert => ({
  id: String(r.id),
  symbol: r.symbol,
  field: r.field === 'Ask' ? 'ask' : 'bid',
  direction: r.direction === 'Below' ? 'below' : 'above',
  price: r.price,
  active: r.active,
  createdAt: r.createdAt,
  triggeredAt: r.triggeredAt ?? undefined,
  triggeredValue: r.triggeredValue ?? undefined,
});

/** The body the server takes for a new alert. */
export const toServer = (a: Pick<PriceAlert, 'symbol' | 'field' | 'direction' | 'price'>) => ({
  symbol: a.symbol,
  field: a.field === 'ask' ? ('Ask' as const) : ('Bid' as const),
  direction: a.direction === 'below' ? ('Below' as const) : ('Above' as const),
  price: a.price,
});

/**
 * Alerts that went off after `since` (UTC ISO), oldest first: what to tell someone who opens the terminal after they went off.
 * With no `since` (first visit on this device) nothing counts as missed.
 */
export function missedSince(alerts: readonly PriceAlert[], since: string | null): PriceAlert[] {
  if (!since) return [];
  const from = Date.parse(since);
  return alerts
    .filter((a) => a.triggeredAt && Date.parse(a.triggeredAt) > from)
    .sort((a, b) => Date.parse(a.triggeredAt!) - Date.parse(b.triggeredAt!));
}

export const CONDITIONS: { value: `${PriceAlert['field']}-${PriceAlert['direction']}`; label: string }[] = [
  { value: 'bid-above', label: 'Bid rises to' },
  { value: 'bid-below', label: 'Bid falls to' },
  { value: 'ask-above', label: 'Ask rises to' },
  { value: 'ask-below', label: 'Ask falls to' },
];

export const conditionLabel = (a: Pick<PriceAlert, 'field' | 'direction'>) =>
  CONDITIONS.find((c) => c.value === `${a.field}-${a.direction}`)!.label;

/** Alerts an older terminal kept in the synced preferences, dropping anything malformed; moved to the server once. */
export function normalizeAlerts(raw: unknown): PriceAlert[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (a): a is PriceAlert =>
        !!a &&
        typeof a === 'object' &&
        typeof (a as PriceAlert).id === 'string' &&
        typeof (a as PriceAlert).symbol === 'string' &&
        ((a as PriceAlert).field === 'bid' || (a as PriceAlert).field === 'ask') &&
        ((a as PriceAlert).direction === 'above' || (a as PriceAlert).direction === 'below') &&
        typeof (a as PriceAlert).price === 'number' &&
        (a as PriceAlert).price > 0,
    )
    .map((a) => ({ ...a, active: a.active === true }))
    .slice(0, MAX_ALERTS);
}

interface Quote {
  bid: number;
  ask: number;
  fresh?: boolean;
}

/** Whether an active alert goes off at this quote. Stale quotes never set one off. */
export function crossed(alert: PriceAlert, quote: Quote | undefined): boolean {
  if (!alert.active || !quote || quote.fresh === false) return false;
  const value = quote[alert.field];
  return alert.direction === 'above' ? value >= alert.price : value <= alert.price;
}

/** A problem with a new alert, or null. One that would go off at once is refused: it is almost always a typo. */
export function newAlertProblem(
  draft: { symbol: string; field: PriceAlert['field']; direction: PriceAlert['direction']; price: number },
  quote: Quote | undefined,
  count: number,
): string | null {
  if (count >= MAX_ALERTS) return `You can keep up to ${MAX_ALERTS} alerts. Delete some first.`;
  if (!draft.symbol) return 'Choose a symbol.';
  if (!(draft.price > 0)) return 'Enter the price as a positive number.';
  if (quote && crossed({ ...draft, id: '', active: true, createdAt: '' }, quote)) {
    const now = quote[draft.field];
    return `The ${draft.field} is already ${draft.direction === 'above' ? 'at or above' : 'at or below'} that (${now}), so it would go off at once.`;
  }
  return null;
}
